import { useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { GlobalSearch } from './GlobalSearch'
import { UserMenu } from './UserMenu'
import { NotificationBell } from './NotificationBell'
import { usePageHeaderStore } from './pageHeader'

/**
 * The header (D-115, D-120): where you are — home, then the module — or a page's own
 * title where it names itself; search and the account menu top right, on white.
 *
 * Below the lg breakpoint the sidebar folds away behind a menu button. A 214px
 * column is a third of a laptop's width but more than half a phone's; left in
 * place it leaves a till or a debtor's account about 170px to live in.
 */
export function AppShell({ title, children }: { title?: string; children?: ReactNode }) {
  const pageTitle = usePageHeaderStore((s) => s.title)
  const pageSubtitle = usePageHeaderStore((s) => s.subtitle)
  const setSlot = usePageHeaderStore((s) => s.setSlot)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const navigated = useCallback(() => setMenuOpen(false), [])
  const closed = useCallback(() => {
    setMenuOpen(false)
    menuButton.current?.focus()
  }, [])

  return (
    // From lg up the content scrolls beside a fixed sidebar. Below it the page itself
    // scrolls under a pinned header: a phone browser only folds its address bar away,
    // and only jumps to the top on a tap of the status bar, when the document scrolls.
    // (h-full, not h-screen: 100vh on a phone is the height with the toolbar hidden.)
    <div className="flex min-h-full bg-dpl-bg lg:h-full lg:overflow-hidden">
      <Sidebar open={menuOpen} onNavigate={navigated} onClose={closed} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 shrink-0 border-b border-dpl-border bg-dpl-surface lg:static">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-6 lg:py-4">
            <div className="flex min-w-0 items-center gap-2">
              <button
                ref={menuButton}
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
                aria-controls="app-sidebar"
                className="-ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-dpl-text hover:bg-dpl-bg focus:outline-none focus:ring-2 focus:ring-dpl-primary/40 lg:hidden"
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                  <path d="M3 5.5h14M3 10h14M3 14.5h14" />
                </svg>
              </button>
              {pageTitle ? (
                // A page that names itself in the bar, as the till does (its mockup).
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-bold text-dpl-text lg:text-2xl">{pageTitle}</h1>
                  {pageSubtitle && <p className="truncate text-xs text-dpl-muted sm:text-sm">{pageSubtitle}</p>}
                </div>
              ) : (
                // Everywhere else, where you are: home, then the module. The page's own title is in the page.
                <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-sm">
                  <Link to="/" aria-label="Home" className="hidden shrink-0 rounded p-1 text-dpl-muted hover:text-dpl-text sm:block">
                    <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 9.5 10 3.5l7 6" />
                      <path d="M5 8v8.5h4V12h2v4.5h4V8" />
                    </svg>
                  </Link>
                  <span className="hidden text-dpl-muted sm:inline" aria-hidden="true">›</span>
                  <h1 className="truncate text-base font-semibold text-dpl-text sm:text-sm sm:font-medium">{title ?? ''}</h1>
                </nav>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-3 sm:gap-5">
              {/* A page's own controls (the till's connection and clock) land here. */}
              <div ref={setSlot} className="flex items-center gap-3 empty:hidden sm:gap-5" />
              {!pageTitle && <GlobalSearch />}
              <NotificationBell />
              <UserMenu />
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:overflow-y-auto">{children ?? <Outlet />}</main>
      </div>
    </div>
  )
}
